/**
 * LangProvider — bilingual (ar/en) language context + RTL direction
 * controller. Extracted from `App.tsx` per ticket superall-shell-01.
 *
 * - Persists the active language to localStorage under SUPERALL_LANG_KEY.
 * - Defaults to "ar" (Saudi consumer surface) per ksa-locale-architect.
 * - Sets `document.documentElement.dir` + `lang` attributes on mount
 *   and on every toggle so RTL flips correctly across every page.
 *
 * Consumers: `useT()` returns the active dict; `useLang()` returns the
 * `[lang, setLang]` tuple.
 */

import * as React from "react";

import {
  SUPERALL_LANG_KEY,
  STRINGS,
  type Lang,
  type ActiveDict,
} from "../i18n/strings";

export type { Lang, ActiveDict } from "../i18n/strings";

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: ActiveDict;
}

const LangContext = React.createContext<LangContextValue | null>(null);

function getStoredLang(): Lang {
  if (typeof window === "undefined") return "ar";
  const raw = localStorage.getItem(SUPERALL_LANG_KEY);
  return raw === "en" || raw === "ar" ? raw : "ar";
}

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = React.useState<Lang>(getStoredLang);

  // Sync `dir` + `lang` attributes on the document element so every
  // descendant — including third-party widgets — gets the correct
  // direction without per-component prop drilling.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = React.useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(SUPERALL_LANG_KEY, next);
    } catch {
      /* storage unavailable — direction still applies for the session */
    }
  }, []);

  const value = React.useMemo<LangContextValue>(
    () => ({ lang, setLang, t: STRINGS[lang] }),
    [lang, setLang],
  );

  return (
    <LangContext.Provider value={value}>{children}</LangContext.Provider>
  );
}

export function useLang(): [Lang, (lang: Lang) => void] {
  const ctx = React.useContext(LangContext);
  if (!ctx) {
    throw new Error("useLang must be used within <LangProvider>");
  }
  return [ctx.lang, ctx.setLang];
}

export function useT(): ActiveDict {
  const ctx = React.useContext(LangContext);
  if (!ctx) {
    throw new Error("useT must be used within <LangProvider>");
  }
  return ctx.t;
}
